import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
const BACKGROUND_IMAGES = ['https://cdn.magicpatterns.com/uploads/fssYpKf8swLzdNNaRcNwxG/sj-website-home-banner-1.png', 'https://cdn.magicpatterns.com/uploads/3idp1uY2N511JBaJj5HAAE/sj-website-home-banner-2.png', 'https://cdn.magicpatterns.com/uploads/hWKiPjjFohRqkseJ6Qufg5/sj-website-home-banner-3.png', 'https://cdn.magicpatterns.com/uploads/vMP5ojfesyNXc5k5Zm6z65/sj-website-home-banner-4.png', 'https://cdn.magicpatterns.com/uploads/jrwLvEej2KvpzukSqKcwMX/sj-website-home-banner-5.png'];
// Alternate "Shine!" colour per slide
const SHINE_COLORS = ['#e91e8c', '#1a2744', '#e91e8c', '#1a2744', '#e91e8c'];
export function HeroBanner() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loadedImages, setLoadedImages] = useState<Set<number>>(new Set([0]));
  const [scrollY, setScrollY] = useState(0);
  const [showServices, setShowServices] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Preload the next image before transitioning
  const preloadImage = useCallback((index: number) => {
    if (!loadedImages.has(index)) {
      const img = new Image();
      img.src = BACKGROUND_IMAGES[index];
      img.onload = () => {
        setLoadedImages(prev => new Set(prev).add(index));
      };
    }
  }, [loadedImages]);
  useEffect(() => {
    // Preload the next image ahead of time
    const nextIndex = (currentImageIndex + 1) % BACKGROUND_IMAGES.length;
    preloadImage(nextIndex);
    const timer = setInterval(() => {
      setCurrentImageIndex(prev => {
        const next = (prev + 1) % BACKGROUND_IMAGES.length;
        return next;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [currentImageIndex, preloadImage]);
  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close dropdown on outside click
  useEffect(() => {
    if (!showServices) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowServices(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showServices]);
  const shineColor = SHINE_COLORS[currentImageIndex] || '#e91e8c';
  // Only render current + next slide to minimize initial load
  // Previous slide is only needed after first transition
  const visibleIndices = new Set(currentImageIndex === 0 ? [0, 1] : [currentImageIndex, (currentImageIndex + 1) % BACKGROUND_IMAGES.length, (currentImageIndex - 1 + BACKGROUND_IMAGES.length) % BACKGROUND_IMAGES.length]);
  return <div className="relative w-full h-screen min-h-[600px] overflow-hidden bg-gray-100 flex flex-col" data-id="element-146">
      {/* Background Slideshow — only render current + adjacent slides */}
      {BACKGROUND_IMAGES.map((img, idx) => {
      if (!visibleIndices.has(idx)) return null;
      return <div key={idx} className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${idx === currentImageIndex ? 'opacity-100' : 'opacity-0'}`} data-id="element-147">
            <img src={img} alt={`Smith and Jengs professional cleaning service banner ${idx + 1}`} className="w-full h-full object-cover object-center" loading={idx === 0 ? 'eager' : 'lazy'} fetchPriority={idx === 0 ? 'high' : undefined} width={1920} height={1080} data-id="element-148" />
            <div className="absolute inset-0 bg-white/10 mix-blend-overlay pointer-events-none" data-id="element-149" />
          </div>;
    })}

      {/* Content Overlay */}
      <div className="relative z-10 w-full h-full flex flex-col justify-between px-6 pt-24 pb-8 md:px-12 md:pt-36 md:pb-10" data-id="element-150">
        {/* Main Content */}
        <main className="flex-1 flex flex-col justify-start md:justify-center max-w-2xl" data-id="element-151">
          <h1 className="text-[3.5rem] leading-[0.85] md:text-7xl lg:text-[5.5rem] md:leading-[0.8] font-normal text-[#1a2744] font-bebas tracking-wide uppercase min-h-[120px] md:min-h-[150px] lg:min-h-[180px]" data-id="element-152">
            We Shine,
            <br data-id="element-153" />
            When You{' '}
            <span className="font-serif-playfair italic font-bold normal-case text-[3.5rem] md:text-[5rem] lg:text-[5.5rem] leading-none align-baseline inline-block transition-colors duration-1000" style={{
            color: shineColor
          }} data-id="element-154">
              Shine!
            </span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-[#1a2744] max-w-md font-medium" data-id="element-155">
            Residential and Commercial
            <br data-id="element-156" />
            Cleaning Experts
          </p>

          <div className="mt-8 relative" ref={dropdownRef} data-id="element-157">
            <button onClick={() => setShowServices(prev => !prev)} className="px-6 py-2 rounded-full border border-[#e91e8c] text-[#e91e8c] bg-transparent font-semibold flex items-center gap-3 transition-all duration-300 text-sm tracking-wider uppercase" data-id="element-158">
              Book Online
              <span className="inline-block transition-transform duration-300" style={{
              transform: showServices ? 'rotate(90deg)' : 'rotate(0deg)'
            }} data-id="element-159">
                &rarr;
              </span>
            </button>

            <AnimatePresence data-id="element-160">
              {showServices && <motion.div initial={{
              opacity: 0,
              y: -8,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -8,
              scale: 0.95
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} className="absolute left-0 top-full mt-3 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl border border-gray-100 overflow-hidden min-w-[260px] z-50" data-id="element-161">
                  {[{
                label: 'Basic Home Cleaning',
                to: '/basic-home-cleaning'
              }, {
                label: 'Commercial Cleaning',
                to: '/commercial-cleaning'
              }, {
                label: 'Spring / Deep Cleaning',
                to: '/spring-deep-cleaning'
              }].map((service, i) => <Link key={service.to} to={service.to} onClick={() => setShowServices(false)} className="flex items-center gap-3 px-5 py-3.5 text-[#1a2744] hover:bg-[#e91e8c]/10 hover:text-[#e91e8c] transition-colors text-sm font-medium tracking-wide border-b border-gray-100 last:border-b-0" data-id="element-162">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#e91e8c]" data-id="element-163" />
                      {service.label}
                    </Link>)}
                </motion.div>}
            </AnimatePresence>
          </div>
        </main>

        {/* Bottom Section: Large Text & Social Icon */}
        <div className="w-full relative flex justify-center items-end mt-auto pt-4 md:pt-10 overflow-hidden" data-id="element-164">
          <img src="https://cdn.magicpatterns.com/uploads/sUut1eTBvUUEHm5bBaK9Yr/sj-website-sj-text-header.svg" alt="Smith & Jengs" className="w-full h-auto select-none will-change-transform" style={{
          transform: `translateX(-${scrollY * 0.3}px)`
        }} width={1920} height={192} data-id="element-165" />

          {/* Facebook Icon */}
          <div className="absolute right-0 bottom-12 md:bottom-16" data-id="element-166">
            <a href="https://www.facebook.com/SmithandJengs/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-white/80 flex items-center justify-center text-[#1a2744] hover:bg-white hover:text-[#e91e8c] transition-colors shadow-sm" aria-label="Visit Smith and Jengs on Facebook" data-id="element-167">
              <Facebook className="w-4 h-4 fill-current" data-id="element-168" />
            </a>
          </div>
        </div>
      </div>
    </div>;
}